import apiTamboUrl from "@/services/api";
import db from "@/services/offlineDb";
import { enqueueOp } from "@/services/offlineQueue";

export default {
  namespaced: true,
  state: {
    corrales: [],
    vistaCorrales: [],
    resumenTotal: null,
    animales: [],
    animalesMuertos: [],
    animalesAbortos: [],
    ventasPreparacion: [],
    ventasHistorial: [],
    tropasHistorial: [],
    estadisticas: null,
    operacionesCompra: [],
    alertasPago: [],
    loading: false,
    error: null,
  },
  getters: {
    corrales: (s) => s.corrales,
    vistaCorrales: (s) => s.vistaCorrales,
    resumenTotal: (s) => s.resumenTotal,
    animales: (s) => s.animales,
    animalesMuertos: (s) => s.animalesMuertos,
    animalesAbortos: (s) => s.animalesAbortos,
    ventasPreparacion: (s) => s.ventasPreparacion,
    ventasHistorial: (s) => s.ventasHistorial,
    tropasHistorial: (s) => s.tropasHistorial,
    estadisticas: (s) => s.estadisticas,
    operacionesCompra: (s) => s.operacionesCompra,
    alertasPago: (s) => s.alertasPago,
    loading: (s) => s.loading,
    error: (s) => s.error,

    animalesPorCorralTipo: (s) => (tipo) => {
      return s.animales.filter(
        (a) => a.corral_actual && a.corral_actual.tipo === tipo
      );
    },

    corralesPorTipo: (s) => (tipo) => {
      return s.corrales.filter((c) => c.tipo === tipo);
    },
  },
  mutations: {
    setCorrales(state, data) {
      state.corrales = data;
    },
    setVistaCorrales(state, data) {
      state.vistaCorrales = data;
    },
    setResumenTotal(state, data) {
      state.resumenTotal = data;
    },
    setAnimales(state, data) {
      state.animales = data;
    },
    setAnimalesMuertos(state, data) {
      state.animalesMuertos = data;
    },
    setAnimalesAbortos(state, data) {
      state.animalesAbortos = data;
    },
    setVentasPreparacion(state, data) {
      state.ventasPreparacion = data;
    },
    setVentasHistorial(state, data) {
      state.ventasHistorial = data;
    },
    setTropasHistorial(state, data) {
      state.tropasHistorial = data;
    },
    setEstadisticas(state, data) {
      state.estadisticas = data;
    },
    setOperacionesCompra(state, data) {
      state.operacionesCompra = data;
    },
    setAlertasPago(state, data) {
      state.alertasPago = data;
    },
    setLoading(state, v) {
      state.loading = v;
    },
    setError(state, v) {
      state.error = v;
    },
    clearState(state) {
      state.corrales = [];
      state.vistaCorrales = [];
      state.resumenTotal = null;
      state.animales = [];
      state.ventasPreparacion = [];
      state.ventasHistorial = [];
      state.estadisticas = null;
      state.operacionesCompra = [];
      state.alertasPago = [];
      state.error = null;
    },
  },
  actions: {
    // ─── CORRALES ───
    async getCorrales({ commit }) {
      commit("setLoading", true);
      try {
        const { data } = await apiTamboUrl.get("/feedlot/corrales");
        commit("setCorrales", data);
        // Guardar en Dexie para offline
        if (Array.isArray(data) && data.length > 0) {
          await db.corrales_feedlot.bulkPut(data).catch(() => {});
        }
      } catch (e) {
        if (!navigator.onLine) {
          const local = await db.corrales_feedlot.toArray();
          commit("setCorrales", local);
        } else {
          commit("setError", e.message);
        }
      } finally {
        commit("setLoading", false);
      }
    },

    async crearCorral({ dispatch }, payload) {
      const { data } = await apiTamboUrl.post("/feedlot/corrales", payload);
      await dispatch("getCorrales");
      return data;
    },

    async actualizarCorral({ dispatch }, { id, data: payload }) {
      const { data } = await apiTamboUrl.patch(
        `/feedlot/corrales/${id}`,
        payload
      );
      await dispatch("getCorrales");
      return data;
    },

    async eliminarCorral({ dispatch }, id) {
      const { data } = await apiTamboUrl.delete(`/feedlot/corrales/${id}`);
      await dispatch("getCorrales");
      return data;
    },

    async getVistaCorrales({ commit }) {
      try {
        const { data } = await apiTamboUrl.get("/feedlot/corrales/vista");
        commit("setVistaCorrales", data);
        // Guardar en Dexie para offline
        if (Array.isArray(data) && data.length > 0) {
          await db.corrales_feedlot
            .bulkPut(data.map((v) => ({ ...v, _vista: true })))
            .catch(() => {});
        }
      } catch (e) {
        if (!navigator.onLine) {
          // Sin conexión: usar corrales ya cargados como fallback (vista será vacía)
          commit("setVistaCorrales", []);
        } else {
          commit("setError", e.message);
        }
      }
    },

    async getResumenTotal({ commit }) {
      try {
        const { data } = await apiTamboUrl.get(
          "/feedlot/corrales/resumen-total"
        );
        commit("setResumenTotal", data);
      } catch (e) {
        commit("setError", e.message);
      }
    },

    // ─── ANIMALES ───
    async getAnimales(
      { commit },
      { corralTipo = null, estado_excepcion = null } = {}
    ) {
      commit("setLoading", true);
      try {
        const params = {};
        if (corralTipo) params.corral_tipo = corralTipo;
        if (estado_excepcion) params.estado_excepcion = estado_excepcion;
        const { data } = await apiTamboUrl.get("/feedlot/animales", { params });
        const items = Array.isArray(data) ? data : data.items || [];

        // No guardamos offline las excepciones, solo el listado principal vivo
        if (!estado_excepcion) {
          commit("setAnimales", items);
          if (items.length > 0) {
            await db.animales.bulkPut(items).catch(() => {});
          }
        }
        return items;
      } catch (e) {
        if (!navigator.onLine && !estado_excepcion) {
          const local = await db.animales.toArray();
          commit("setAnimales", local);
          return local;
        } else {
          commit("setError", e.message);
          return [];
        }
      } finally {
        commit("setLoading", false);
      }
    },

    async getMuertosFeedlot({ dispatch, commit }) {
      const data = await dispatch("getAnimales", {
        estado_excepcion: "muerto",
      });
      commit("setAnimalesMuertos", data);
      return data;
    },

    async getAbortosFeedlot({ dispatch, commit }) {
      const data = await dispatch("getAnimales", {
        estado_excepcion: "aborto",
      });
      commit("setAnimalesAbortos", data);
      return data;
    },

    // ─── CAPADO ───
    async confirmarCapado({ dispatch }, animalId) {
      const { data } = await apiTamboUrl.post(`/feedlot/capado/${animalId}`);
      await dispatch("getAnimales");
      return data;
    },

    // ─── DESTETE ───
    async confirmarDestete({ dispatch }, params) {
      const animalId = typeof params === "object" ? params.animalId : params;
      const payload = typeof params === "object" ? params.payload : null;
      if (!navigator.onLine) {
        await enqueueOp(
          "destetes",
          "create",
          payload,
          `/feedlot/destete/${animalId}`,
          "POST"
        );
        return { detail: "Destete registrado offline", _offline: true };
      }
      const { data } = await apiTamboUrl.post(
        `/feedlot/destete/${animalId}`,
        payload
      );
      await dispatch("getAnimales");
      return data;
    },

    // ─── INGRESO A FEEDLOT ───
    async ingresarAnimal({ dispatch }, { animalId, corralTipo }) {
      const { data } = await apiTamboUrl.post(
        `/feedlot/ingresar/${animalId}`,
        null,
        { params: { corral_tipo: corralTipo } }
      );
      await dispatch("getAnimales");
      return data;
    },

    // ─── CAMBIO DE CORRAL ───
    async cambiarCorral({ dispatch }, { animalId, nuevoCorralTipo }) {
      const { data } = await apiTamboUrl.post(
        `/feedlot/cambiar_corral/${animalId}`,
        null,
        { params: { corral_destino_tipo: nuevoCorralTipo } }
      );
      await dispatch("getAnimales");
      return data;
    },

    async moverAnimalesMasivoFeedlot({ dispatch }, payload) {
      const { data } = await apiTamboUrl.post(
        `/feedlot/corrales/mover`,
        payload
      );
      await dispatch("getAnimales");
      await dispatch("getCorralesVista");
      return data;
    },

    // ─── PESO ───
    async registrarPeso(_, { animalId, payload }) {
      if (!navigator.onLine) {
        await enqueueOp(
          "registros_peso",
          "create",
          payload,
          `/feedlot/peso/${animalId}`,
          "PATCH"
        );
        return { _offline: true, ...payload };
      }
      const { data } = await apiTamboUrl.patch(
        `/feedlot/peso/${animalId}`,
        payload
      );
      return data;
    },

    // ─── ENFERMEDAD ───
    async registrarEnfermedad(_, { animalId, payload }) {
      if (!navigator.onLine) {
        await enqueueOp(
          "enfermedades_feedlot",
          "create",
          payload,
          `/feedlot/enfermedad/${animalId}`,
          "POST"
        );
        return { _offline: true, ...payload };
      }
      const { data } = await apiTamboUrl.post(
        `/feedlot/enfermedad/${animalId}`,
        payload
      );
      return data;
    },

    async toggleEnferma(
      { dispatch },
      { animalId, enferma, detalle = null, enfermedad = null }
    ) {
      const { data } = await apiTamboUrl.post(
        `/grilla_vaca/setear_enferma/${animalId}`,
        { enferma, detalle, enfermedad }
      );
      await dispatch("getAnimales");
      return data;
    },

    async getEnfermedades(_, animalId) {
      const { data } = await apiTamboUrl.get(`/feedlot/enfermedad/${animalId}`);
      return data;
    },

    async marcarFallecido({ dispatch }, { animalId, payload }) {
      const { data } = await apiTamboUrl.post(
        `/feedlot/fallecimiento/${animalId}`,
        payload
      );
      await dispatch("getAnimales");
      return data;
    },

    // ─── RECORRIDOS ───
    async registrarRecorrido(_, { corralId, payload }) {
      if (!navigator.onLine) {
        await enqueueOp(
          "recorridos_corral",
          "create",
          payload,
          `/feedlot/recorrido/${corralId}`,
          "POST"
        );
        return { _offline: true, ...payload };
      }
      const { data } = await apiTamboUrl.post(
        `/feedlot/recorrido/${corralId}`,
        payload
      );
      return data;
    },

    async getRecorridos(_, { corralId, params = {} }) {
      const { data } = await apiTamboUrl.get(
        `/feedlot/recorridos/${corralId}`,
        { params }
      );
      return data;
    },

    async getHistorialCorral(_, { corralId, params = {} }) {
      const { data } = await apiTamboUrl.get(
        `/feedlot/corrales/${corralId}/historial`,
        { params }
      );
      return data;
    },

    // ─── DETALLE ANIMAL ───
    async getDetalleAnimal(_, animalId) {
      const { data } = await apiTamboUrl.get(`/feedlot/animales/${animalId}`);
      return data;
    },

    // ─── PENDIENTES ───
    async getPendientes() {
      const { data } = await apiTamboUrl.get("/feedlot/pendientes");
      return data;
    },

    // ─── BÚSQUEDA POR CARAVANA ───
    async buscarPorCaravana({ commit }, { caravana }) {
      const params = new URLSearchParams();
      // Texto (`AR-101`): se manda tal cual, el backend la normaliza.
      params.append("caravana", String(caravana ?? "").trim());
      const { data } = await apiTamboUrl.get(
        `/feedlot/animales?${params.toString()}`
      );
      const items = Array.isArray(data) ? data : data.items || [];
      commit("setAnimales", items);
      return items;
    },

    // ─── VACUNAS ───
    async getVacunasChecklist(_, animalId) {
      const { data } = await apiTamboUrl.get(
        `/feedlot/vacunas/checklist/${animalId}`
      );
      return data;
    },

    async aplicarVacuna(_, { animalId, payload }) {
      const { data } = await apiTamboUrl.post(
        `/feedlot/vacunas/aplicar/${animalId}`,
        payload
      );
      return data;
    },

    // ─── DIETA HISTORIAL ───
    async getDietaHistorial(_, animalId) {
      const { data } = await apiTamboUrl.get(
        `/feedlot/dieta-historial/${animalId}`
      );
      return data;
    },

    // ─── CRÍA ───
    async registrarCria(_, payload) {
      const { data } = await apiTamboUrl.post(
        "/feedlot/registrar-cria",
        payload
      );
      return data;
    },

    // ─── VENTAS ───
    async prepararVenta({ dispatch }, { animalId, payload }) {
      const { data } = await apiTamboUrl.post(
        `/feedlot/ventas/preparar/${animalId}`,
        payload
      );
      await dispatch("getVentasPreparacion");
      return data;
    },

    async confirmarVenta({ dispatch }, { ventaId, payload }) {
      const { data } = await apiTamboUrl.post(
        `/feedlot/ventas/${ventaId}/confirmar`,
        payload
      );
      await dispatch("getVentasPreparacion");
      return data;
    },

    async editarVenta({ dispatch }, { ventaId, payload }) {
      const { data } = await apiTamboUrl.patch(
        `/feedlot/ventas/${ventaId}`,
        payload
      );
      await dispatch("getVentasPreparacion");
      return data;
    },

    async cancelarVenta({ dispatch }, ventaId) {
      await apiTamboUrl.delete(`/feedlot/ventas/${ventaId}/cancelar`);
      await dispatch("getVentasPreparacion");
    },

    async getVentasPreparacion({ commit }) {
      try {
        const { data } = await apiTamboUrl.get("/feedlot/ventas/preparacion");
        commit("setVentasPreparacion", data);
      } catch (e) {
        commit("setError", e.message);
      }
    },

    async getVentasHistorial({ commit }, params = {}) {
      try {
        const { data } = await apiTamboUrl.get("/feedlot/ventas/historial", {
          params,
        });
        const items = Array.isArray(data) ? data : data.items || [];
        commit("setVentasHistorial", items);
      } catch (e) {
        commit("setError", e.message);
      }
    },

    async crearTropa({ dispatch }, payload) {
      const { data } = await apiTamboUrl.post("/feedlot/tropas", payload);
      await dispatch("getTropas");
      return data;
    },

    async postAnimalesBulk({ dispatch }, payload) {
      const { data } = await apiTamboUrl.post("/feedlot/animales/bulk", payload);
      await dispatch("getAnimales");
      return data;
    },

    async getTropas({ commit }) {
      try {
        const { data } = await apiTamboUrl.get("/feedlot/tropas");
        commit("setTropasHistorial", data);
      } catch (e) {
        commit("setError", e.message);
      }
    },

    // ─── CARGA EXCEL ───
    async uploadExcelPreview(_, formData) {
      const { data } = await apiTamboUrl.post(
        "/feedlot/upload-excel/preview",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data;
    },

    async uploadExcelCommit(_, { formData, autoGenerateCaravana = false }) {
      const url = `/feedlot/upload-excel/commit?auto_generate_caravana=${autoGenerateCaravana}`;
      const { data } = await apiTamboUrl.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },

    // ─── ESTADÍSTICAS ───
    async getEstadisticas({ commit }, params = {}) {
      try {
        const { data } = await apiTamboUrl.get("/feedlot/estadisticas", {
          params,
        });
        commit("setEstadisticas", data);
      } catch (e) {
        commit("setError", e.message);
      }
    },

    async getEstadisticasGananciaPeso(_, params = {}) {
      const { data } = await apiTamboUrl.get(
        "/feedlot/estadisticas/ganancia-peso",
        { params }
      );
      return data;
    },

    async getEstadisticasBalance(_, params = {}) {
      const { data } = await apiTamboUrl.get("/feedlot/estadisticas/balance", {
        params,
      });
      return data;
    },

    async getEstadisticasDietasPeso(context, params = {}) {
      const { data } = await apiTamboUrl.get(
        "/feedlot/estadisticas/dietas-peso",
        { params }
      );
      return data;
    },

    async getEstadisticasTransiciones() {
      const { data } = await apiTamboUrl.get(
        "/feedlot/estadisticas/transiciones-corral"
      );
      return data;
    },

    // ─── OPERACIONES DE COMPRA (feedlot) ───
    async crearOperacionCompra({ dispatch }, payload) {
      const { data } = await apiTamboUrl.post("/feedlot/compras", payload);
      await dispatch("getOperacionesCompra");
      return data;
    },

    async getOperacionesCompra({ commit }, params = {}) {
      try {
        const { data } = await apiTamboUrl.get("/feedlot/compras", { params });
        commit("setOperacionesCompra", data);
      } catch (e) {
        commit("setError", e.message);
      }
    },

    async getAlertasPago({ commit }) {
      try {
        const { data } = await apiTamboUrl.get("/feedlot/compras/alertas-pago");
        commit("setAlertasPago", data);
      } catch (e) {
        commit("setError", e.message);
      }
    },

    async getOperacionCompra(_, operacionId) {
      const { data } = await apiTamboUrl.get(`/feedlot/compras/${operacionId}`);
      return data;
    },

    async actualizarPagoCompra({ dispatch }, { operacionId, payload }) {
      const { data } = await apiTamboUrl.put(
        `/feedlot/compras/${operacionId}/pago`,
        payload
      );
      await dispatch("getOperacionesCompra");
      return data;
    },

    async postAnimalesBulk({ dispatch }, payload) {
      const { data } = await apiTamboUrl.post("/feedlot/animales/bulk", payload);
      await dispatch("getAnimales");
      return data;
    },
  },
};

